import { useSyncExternalStore } from 'react';
import { readStorage, writeStorage } from '../../lib/safeStorage';
import { logLocalSetting } from '../../lib/localSettingLog';
import { StorageKey } from '../../lib/storageKeys';

/** The three columns of the wide day view (data dictionary §7, `wiztime.columnOrder`). */
export const DayColumn = {
  Timeline: 'timeline',
  QuickMark: 'quickMark',
  Checkin: 'checkin',
} as const;
export type DayColumn = (typeof DayColumn)[keyof typeof DayColumn];

export const DAY_COLUMNS: readonly DayColumn[] = [DayColumn.QuickMark, DayColumn.Timeline, DayColumn.Checkin];

/** Default order, left to right: quick mark, timeline, check-ins. */
export const DEFAULT_COLUMN_ORDER: readonly DayColumn[] = DAY_COLUMNS;

/** The stored order when it is a permutation of the three columns; the default otherwise. */
export function parseColumnOrder(raw: string | null): readonly DayColumn[] {
  if (raw === null) return DEFAULT_COLUMN_ORDER;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return DEFAULT_COLUMN_ORDER;
  }
  if (!Array.isArray(parsed) || parsed.length !== DAY_COLUMNS.length) return DEFAULT_COLUMN_ORDER;
  const valid = DAY_COLUMNS.every((column) => parsed.includes(column));
  return valid ? (parsed as DayColumn[]) : DEFAULT_COLUMN_ORDER;
}

/** `order` with `column` moved to `position`; the column that held `position` takes its old place. */
export function placeColumn(order: readonly DayColumn[], position: number, column: DayColumn): DayColumn[] {
  const next = [...order];
  const from = next.indexOf(column);
  const displaced = next[position];
  if (from < 0 || displaced === undefined) return next;
  next[position] = column;
  next[from] = displaced;
  return next;
}

const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  window.addEventListener('storage', listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', listener);
  };
}

/** Raw string as the snapshot: React compares it by value, so the parsed array is built per render. */
function snapshot(): string {
  return readStorage(StorageKey.ColumnOrder) ?? '';
}

export function setColumnOrder(order: readonly DayColumn[]): void {
  writeStorage(StorageKey.ColumnOrder, JSON.stringify(order));
  listeners.forEach((listener) => listener());
  logLocalSetting(StorageKey.ColumnOrder, order);
}

/** Column order of the wide day view, kept on this device and live across tabs. */
export function useColumnOrder(): readonly DayColumn[] {
  const raw = useSyncExternalStore(subscribe, snapshot, () => '');
  return parseColumnOrder(raw === '' ? null : raw);
}
